import React, { useState, useMemo } from "react";

interface SocialMediaProps {
  formData: {
    phone: string;
    firstName: string;
    lastName: string;
    socialMedia?: {
      instagram?: string;
      facebook?: string;
      linkedin?: string;
      tiktok?: string;
    };
  };
  onUpdate: (field: string, value: any) => void;
  errors: { [key: string]: string };
}

const SOCIAL_MEDIA_PLATFORMS = [
  {
    key: "instagram",
    name: "Instagram",
    color: "bg-pink-600/10 text-pink-700 border-pink-600/30",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path
          d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm5.75-2.5a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 17.75 7Z"
          fill="currentColor"
        />
      </svg>
    ),
    placeholder: "https://www.instagram.com/your.handle"
  },
  {
    key: "facebook",
    name: "Facebook",
    color: "bg-blue-600/10 text-blue-700 border-blue-600/30",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path
          d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06C2 17.06 5.66 21.21 10.44 22v-6.99H7.9v-2.95h2.54V9.41c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.23.2 2.23.2v2.45h-1.25c-1.23 0-1.61.77-1.61 1.56v1.87h2.74l-.44 2.95h-2.3V22C18.34 21.21 22 17.06 22 12.06Z"
          fill="currentColor"
        />
      </svg>
    ),
    placeholder: "https://www.facebook.com/your.page"
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    color: "bg-sky-600/10 text-sky-700 border-sky-600/30",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path
          d="M4.98 3.5A2.5 2.5 0 1 1 2.5 6 2.5 2.5 0 0 1 4.98 3.5ZM3 8h4v12H3Zm6.5 0h3.84v1.64h.05A4.2 4.2 0 0 1 18 7.72C20.77 7.72 22 9.44 22 12.59V20h-4v-6.44c0-1.54-.55-2.6-1.93-2.6a2.08 2.08 0 0 0-1.95 1.39 2.6 2.6 0 0 0-.12.93V20h-4Z"
          fill="currentColor"
        />
      </svg>
    ),
    placeholder: "https://www.linkedin.com/in/your-handle"
  },
  {
    key: "tiktok",
    name: "TikTok",
    color: "bg-gray-700/10 text-gray-800 border-gray-700/30",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path
          d="M14.5 2h2.5a6.5 6.5 0 0 0 6.5 6.5V11A8.5 8.5 0 1 1 15 2.07V8a4.5 4.5 0 1 0 3 4.24V9.78a6.5 6.5 0 0 1-3.5-1.78Z"
          fill="currentColor"
        />
      </svg>
    ),
    placeholder: "https://www.tiktok.com/@your.handle"
  },
  {
    key: "youtube",
    name: "YouTube",
    color: "bg-red-600/10 text-red-700 border-red-600/30",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path
          d="M23.5 7.2s-.23-1.64-.94-2.36a3.3 3.3 0 0 0-2.31-.97C17.8 3.5 12 3.5 12 3.5h0s-5.8 0-8.25.37c-.9.1-1.73.53-2.31.97C.73 5.56.5 7.2.5 7.2S.25 9.18.25 11.15v1.68c0 1.97.25 3.95.25 3.95s.23 1.64.94 2.36c.7.72 1.62.7 2.04.78C5.06 20.22 12 20.25 12 20.25s5.8 0 8.25-.37c.9-.1 1.73-.53 2.31-.97.7-.72.94-2.36.94-2.36s.25-1.98.25-3.95v-1.68c0-1.97-.25-3.95-.25-3.95ZM9.75 14.75V8.75l6 3Z"
          fill="currentColor"
        />
      </svg>
    ),
    placeholder: "https://www.youtube.com/@your.channel"
  },
  {
    key: "x",
    name: "X (Twitter)",
    color: "bg-gray-800/10 text-gray-900 border-gray-800/30",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path
          d="M18.244 2H21L13.5 10.67 22 22h-6.56l-4.92-6.44L4.8 22H2l7.98-9.13L2 2h6.72l4.45 5.87L18.244 2Zm-1.15 18h1.71L7.98 4h-1.7l10.814 16Z"
          fill="currentColor"
        />
      </svg>
    ),
    placeholder: "https://x.com/your.handle"
  },
  {
    key: "whatsapp",
    name: "WhatsApp",
    color: "bg-green-600/10 text-green-700 border-green-600/30",
    icon: (
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path
          d="M12.04 2C6.51 2 2 6.34 2 11.68c0 2.01.65 3.87 1.75 5.42L2 22l5.06-1.65a10.35 10.35 0 0 0 4.98 1.27c5.53 0 10.04-4.34 10.04-9.68C22.08 6.34 17.57 2 12.04 2Zm.02 17.2c-1.57 0-3.08-.45-4.36-1.3l-.31-.2-3 .98.99-2.9-.2-.3a7.49 7.49 0 0 1-1.14-3.95c0-4.16 3.53-7.55 7.9-7.55 4.36 0 7.9 3.39 7.9 7.55 0 4.16-3.54 7.55-7.9 7.55Zm4.36-5.7c-.24-.12-1.41-.69-1.63-.76-.22-.08-.38-.12-.54.12-.16.24-.62.76-.76.91-.14.16-.28.18-.52.06-.24-.12-1.02-.37-1.95-1.17-.72-.6-1.21-1.33-1.35-1.56-.14-.24-.01-.36.11-.48.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.77-.2-.48-.4-.4-.54-.4h-.46c-.16 0-.42.06-.64.3-.22.24-.84.8-.84 1.95 0 1.15.86 2.26.98 2.42.12.16 1.7 2.62 4.1 3.56.57.25 1.02.4 1.37.51.57.18 1.09.16 1.5.1.46-.07 1.41-.58 1.61-1.13.2-.56.2-1.03.14-1.13-.06-.1-.22-.16-.46-.28Z"
          fill="currentColor"
        />
      </svg>
    ),
    placeholder: "https://wa.me/phoneNumber"
  },
  {
    key: "slack",
    name: "Slack",
    color: "bg-[#4A154B]/10 text-[#4A154B] border-[#4A154B]/30",
    icon: (
      // Slack logo SVG
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <g>
          <path
            d="M5.229 14.154a2.096 2.096 0 1 1-2.095-2.095h2.095v2.095zm1.049 0a2.096 2.096 0 1 1 4.191 0v5.242a2.096 2.096 0 1 1-4.192 0v-5.242zm2.096-7.336a2.096 2.096 0 1 1 2.096-2.096v2.096H8.374zm0 1.05A2.096 2.096 0 0 1 6.278 9.963H1.036A2.096 2.096 0 1 1 1.036 5.77h5.242a2.096 2.096 0 0 1 2.096 2.096zm7.337 2.095a2.096 2.096 0 1 1 0-4.191h5.242a2.096 2.096 0 1 1 0 4.192h-5.242zm-1.05-2.095a2.096 2.096 0 0 1-2.095-2.096V1.036a2.096 2.096 0 1 1 4.192 0v5.242a2.096 2.096 0 0 1-2.096 2.096zm2.095 7.336a2.096 2.096 0 1 1 2.096 2.096h-2.096v-2.096zm0-1.05a2.096 2.096 0 0 1 2.096-2.095h5.242a2.096 2.096 0 1 1 0 4.191h-5.242a2.096 2.096 0 0 1-2.096-2.096zm-7.337-2.095a2.096 2.096 0 1 1 0 4.191H1.036a2.096 2.096 0 1 1 0-4.191h5.242z"
            fill="currentColor"
          />
        </g>
      </svg>
    ),
    placeholder: "https://yourworkspace.slack.com"
  },
  {
    key: "vimeo",
    name: "Vimeo",
    color: "bg-blue-400/10 text-blue-500 border-blue-400/30",
    icon: (
      // Vimeo logo SVG
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <path
          d="M22.23 7.24c-.11 2.35-1.74 5.58-4.89 9.68-3.27 4.2-6.02 6.31-8.27 6.31-1.4 0-2.59-1.29-3.57-3.87-.66-2.4-1.31-4.8-1.97-7.21-.73-2.51-1.51-3.78-2.32-3.78-.18 0-.82.39-1.93 1.18l-1.16-1.5c1.21-1.07 2.42-2.15 3.63-3.24 1.64-1.43 2.89-2.18 3.76-2.25 1.98-.15 3.2 1.15 3.64 3.91.49 3.08.83 5 .99 5.77.55 2.48 1.16 3.7 1.83 3.7.52 0 1.3-.81 2.34-2.43 1.04-1.63 1.6-2.86 1.68-3.69.15-1.39-.4-2.09-1.65-2.09-.59 0-1.19.13-1.8.38 1.19-3.88 3.46-5.75 6.81-5.6 2.49.11 3.66 1.69 3.52 4.73z"
          fill="currentColor"
        />
      </svg>
    ),
    placeholder: "https://vimeo.com/yourprofile"
  }
] as const;

type PlatformKey = typeof SOCIAL_MEDIA_PLATFORMS[number]["key"];

type Selection = {
  url: string;
  isEditing: boolean;
};

type Selections = Record<PlatformKey, Selection | undefined>;

export const SocialMedia: React.FC<SocialMediaProps> = ({
  formData,
  onUpdate,
  errors,
}) => {
  // Initialize selections from formData
  const getInitialSelections = (): Selections => {
    const socialMedia = formData.socialMedia || {};
    const selections: Partial<Selections> = {};
    
    Object.entries(socialMedia).forEach(([platform, url]) => {
      if (url && url.trim()) {
        selections[platform as PlatformKey] = {
          url: url.trim(),
          isEditing: false
        };
      }
    });
    
    return selections as Selections;
  };

  const [selections, setSelections] = useState<Selections>(getInitialSelections);


  // Optimized toggle logic: always use updater-style for safety, keep pure, clean control flow
  const handleToggle = (key: PlatformKey) => {
    setSelections((prev) => {
      const existing = prev[key];
      if (existing) {
        // If already editing, try to save; if not, enter edit mode
        if (existing.isEditing) {
          // Save the current url (trimmed)
          handleSave(key, existing.url);
        } else {
          // Enter editing mode for this platform
          return {
            ...prev,
            [key]: { ...existing, isEditing: true }
          };
        }
        return prev;
      }
      // Add new platform entry and immediately enter edit mode
      return {
        ...prev,
        [key]: { url: "", isEditing: true }
      };
    });
  };

  const handleRemove = (key: PlatformKey) => {
    setSelections((prev) => {
      const next = { ...prev };
      delete next[key];
      updateFormData(next);
      return next;
    });
  };

  const handleSave = (key: PlatformKey, url: string) => {
    setSelections((prev) => {
      const updated = {
        ...prev,
        [key]: { url: url.trim(), isEditing: false }
      };
      updateFormData(updated);
      return updated;
    });
  };

  const handleEdit = (key: PlatformKey) => {
    
    setSelections((prev) => ({
      ...prev,
      [key]: { ...(prev[key] as Selection), isEditing: true }
    }));
  };

  const updateFormData = (updatedSelections: Selections) => {
    const socialMedia: { [key: string]: string } = {};
    Object.entries(updatedSelections).forEach(([platform, selection]) => {
      if (selection && selection.url.trim()) {
        socialMedia[platform] = selection.url;
      }
    });
    onUpdate("socialMedia", socialMedia);
  };

  return (
    <div className="form-step active">
      <p style={{ textAlign: "center", color: "#666", marginBottom: "2rem" }}>
        Add your social media profiles (all optional)
      </p>

      {/* Icons grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
        {SOCIAL_MEDIA_PLATFORMS.map((platform) => {
          const selected = !!selections[platform.key];
          return (
            <div
              key={platform.key}
              style={{
                position: "relative",
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleToggle(platform.key);


                }}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem",
                  width: "100%",
                  padding: "1rem",
                  border: selected
                    ? "2px solid #007bff"
                    : "2px solid #e0e0e0",
                  borderRadius: "12px",
                  backgroundColor: selected ? "#f0f8ff" : "white",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  overflow: "hidden",
                  minHeight: selected ? "auto" : "120px"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.75rem",
                    width: "100%",
                    overflow: "hidden"
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: selected ? "#007bff" : "#f5f5f5",
                      borderRadius: "8px",
                      color: "white",
                    }}
                  >
                    {platform.icon}
                  </div>
                  <div
                    style={{
                      textAlign: "left",
                      flex: 1,
                      width: "100%",
                      overflow: "hidden"
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      {platform.name}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      {selected ? "Selected" : "Click to select"}
                    </div>
                  </div>
                  {/* Inline editor / saved link */}
                  {selected && (
                    <InlineLinkEditor
                      keyId={platform.key}
                      placeholder={platform.placeholder}
                      value={
                        selections[platform.key]?.url ||
                        platform.placeholder ||
                        ""
                      }
                      isEditing={!!selections[platform.key]?.isEditing}
                      onSave={handleSave}
                      onEdit={handleEdit}
                    />
                  )}
                </div>
              </button>

              {/* Red minus indicator */}
              {selected && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(platform.key);
                  }}
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "-8px",
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "#ff4757",
                    color: "white",
                    border: "2px solid white",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                  title="Remove from template"
                >
                  −
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

function InlineLinkEditor({
  keyId,
  placeholder,
  value,
  isEditing,
  onSave,
  onEdit
}: {
  keyId: PlatformKey;
  placeholder: string;
  value: string;
  isEditing: boolean;
  onSave: (key: PlatformKey, url: string) => void;
  onEdit: (key: PlatformKey) => void;
}) {
  const [draft, setDraft] = useState(value || "");
  const [touched, setTouched] = useState(false);

  const isValid = useMemo(() => {
    if (!draft) return false;
    try {
      const u = new URL(draft);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  }, [draft]);

  if (!isEditing) {
    return (
      <div style={{ marginTop: "0.5rem", padding: "0.75rem", border: "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: "#f8f9fa" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "14px", color: "#666" }}>
          
          {value ? (
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              style={{ 
                color: "#007bff", 
                textDecoration: "underline",
                wordBreak: "break-all",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
                maxWidth: "100%"
              }}
            >
              {value}
            </a>
          ) : (
            <span style={{ color: "#999" }}>No link yet — click Edit above to add</span>
          )}
        </div>
      </div>
    );
  }

  const handleSave = () => {
    setTouched(true);
    if (isValid) {
      onSave(keyId, draft);
    }
  };

  return (
    <div style={{ marginTop: "0.5rem", padding: "0.75rem", border: "1px solid #e0e0e0", borderRadius: "8px", backgroundColor: "#f8f9fa" }}>
      <label style={{ display: "block", fontSize: "12px", fontWeight: "500", color: "#333", marginBottom: "0.5rem" }}>
        Profile link
      </label>
      <div style={{ position: "relative" }}>
        
        <input
          type="url"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleSave}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onFocus={(e) => {
            e.stopPropagation();
          }}
          onKeyPress={(e) => {

            if (e.key === "Enter") {
              handleSave();
            }
          }}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "0.5rem 0.5rem 0.5rem 2rem",
            border: touched && !isValid ? "1px solid #ff4757" : "1px solid #ccc",
            borderRadius: "6px",
            fontSize: "14px",
            outline: "none",
            wordBreak: "break-all",
            overflow: "hidden",
            textOverflow: "ellipsis",
            boxSizing: "border-box"
          }}
        />
      </div>
      {touched && !isValid && (
        <p style={{ margin: "0.5rem 0 0 0", fontSize: "12px", color: "#ff4757" }}>
          Enter a valid URL (must start with http:// or https://).
        </p>
      )}
    </div>
  );
}
